import {
  PLAN_UNDO_SECONDS,
  type ActionConsequence,
  type PendingMove,
  type Plan,
  type PlanKind,
  type PlanRun,
  type StepResult,
} from '@cockpit/shared';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { ApiError, api } from '../api';
import {
  TOO_LATE_TOAST,
  UNDONE_TOAST,
  consequencesLink,
  executeBody,
  initialDrafts,
  isFinishedStatus,
  predictedRun,
  runHasResultLink,
  runToastText,
  type StepDraft,
} from '../plans';
import { useAuthStore } from './auth';
import { NET_ONLY_TOAST, useOfflineStore } from './offline';
import { useToastStore } from './toast';

type Phase = 'closed' | 'review' | 'executing' | 'results';

const STAGGER_MS = 220;

/** Lot BI : lectures du plan après l'échéance (petite attente, puis quelques essais espacés : ~13 s en tout). */
const RESULT_POLL_MS = [500, 500, 800, 1000, 1500, 2000, 3000, 4000];

function reducedMotion(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
    : true;
}

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

function errorMessage(err: unknown): string {
  return err instanceof ApiError ? err.message : 'Une erreur inattendue est survenue.';
}

/**
 * Lot BI : options des écrans qui ouvrent un plan. `onScheduled` est appelé dès la validation (le plan part
 * dans 5 secondes, « Annuler » possible) : l'écran peut cacher la carte tout de suite ; la promesse d'`openPlan`
 * se résout ensuite avec l'exécution réelle, ou null si on a annulé (la carte revient).
 */
export interface PlanOpenOptions {
  onScheduled?: () => void;
  /** Lot BQ : le plan vient d'être construit par le serveur (ses dates remplacent l'aperçu du geste). */
  onCreated?: (plan: Plan) => void;
}

/** Lot BQ : plan validé, pas encore exécuté (surcouche du planning, « Annuler » du toast). */
export interface ScheduledPlan {
  plan: Plan;
  checked: string[];
  endsAt: number;
}

/** Lot BQ : une entrée de la pile d'« Annuler » : le toast propose toujours la plus récente. */
export interface UndoEntry {
  id: string;
  text: string;
  endsAt: number;
  run: () => void;
}

/** Lot BI : suite d'un plan programmé (texte du toast, écran de résultats, fin de la promesse de l'écran). */
export interface DeferredFollowUp {
  /** Cases et choix validés (texte du toast, cases de l'écran de résultats). */
  drafts: StepDraft[];
  /** Texte propre à l'écran (ex. « Envoyé avec Gmail… »), calculé sur une exécution ; sinon le résumé. */
  base?: (run: PlanRun) => string;
  /** Rouvrir l'écran de résultats s'il y a des conséquences, un lien ou un échec (comme aujourd'hui). */
  resultsScreen: boolean;
  resolve: (run: PlanRun | null) => void;
  /** Lot BQ : le plan est enregistré dans la surcouche (la couche du geste peut se retirer sans trou). */
  onRegistered?: () => void;
}

/**
 * Écran de validation (CDC §3.4, maquette `#plan`) : le serveur construit le plan, l'utilisateur
 * décoche ce qu'il ne veut pas, puis valide. Rien n'est exécuté avant « Valider et exécuter ». Lot BI : après la
 * validation, rien n'est exécuté pendant 5 secondes (« Annuler » dans le toast) ; c'est le serveur qui exécute à
 * l'échéance, et l'écran lit ensuite le résultat.
 */
export const usePlanStore = defineStore('plan', () => {
  const toast = useToastStore();
  const auth = useAuthStore();
  const plan = ref<Plan | null>(null);
  /** Lot T : cartes ajoutées ou fermées par les règles après l'exécution (bloc « Conséquences »). */
  const consequences = ref<ActionConsequence[]>([]);
  const drafts = ref<StepDraft[]>([]);
  const phase = ref<Phase>('closed');
  /** Résultats affichés une à une (« fait », « erreur », « sauté »). */
  const shown = ref<Record<string, StepResult>>({});
  const error = ref('');
  /** Lot BI : plans validés dont l'exécution attend la fin du délai d'« Annuler » (ou son résultat). */
  const pending = ref<string[]>([]);
  /** Lot BQ : plans validés qui attendent leur échéance, dans l'ordre : lus par la surcouche du planning. */
  const scheduledPlans = ref<ScheduledPlan[]>([]);
  /** Lot BQ : pile d'« Annuler ». Deux plans en attente : aucun des deux ne perd son « Annuler ». */
  const undoStack: UndoEntry[] = [];
  let undoTicket: number | null = null;
  const settledHooks: ((planId: string, plan: Plan) => Promise<void> | void)[] = [];
  let settle: ((run: PlanRun | null) => void) | null = null;
  let openOptions: PlanOpenOptions = {};
  /** Type et références du plan affiché (pour le reconstruire quand on change sa date, lot K). */
  let source: { kind: PlanKind; refs: Record<string, unknown> } | null = null;
  const rebuilding = ref(false);
  /** Dernière exécution, rendue à la fermeture de l'écran de résultats. */
  let lastRun: PlanRun | null = null;

  const isOpen = computed(() => phase.value !== 'closed');

  /** Remet l'écran à zéro ; rend la fonction qui termine la promesse de l'écran (sans l'appeler). */
  function reset(): ((run: PlanRun | null) => void) | null {
    phase.value = 'closed';
    plan.value = null;
    drafts.value = [];
    shown.value = {};
    consequences.value = [];
    error.value = '';
    lastRun = null;
    source = null;
    openOptions = {};
    const resolve = settle;
    settle = null;
    return resolve;
  }

  function close(run: PlanRun | null): void {
    reset()?.(run);
  }

  /** Ouvre l'écran de validation ; la promesse se résout à la fermeture (null si annulé). */
  async function openPlan(
    kind: PlanKind,
    refs: Record<string, unknown> = {},
    options: PlanOpenOptions = {},
  ): Promise<PlanRun | null> {
    // Lot AW : garde centrale. Un plan écrit à l'extérieur (ou relance les règles) : jamais sans réseau, et
    // jamais rejoué tout seul au retour du réseau (il faut le relancer et le valider).
    if (!useOfflineStore().online) {
      toast.show(NET_ONLY_TOAST);
      return null;
    }
    if (settle) close(null);
    let created: Plan;
    try {
      created = await api.createPlan({ kind, refs });
    } catch (err) {
      toast.show(errorMessage(err));
      return null;
    }
    options.onCreated?.(created);
    return present(created, kind, refs, options);
  }

  /** Affiche l'écran de validation d'un plan déjà construit. */
  function present(
    created: Plan,
    kind: PlanKind,
    refs: Record<string, unknown>,
    options: PlanOpenOptions,
  ): Promise<PlanRun | null> {
    if (settle) close(null);
    plan.value = created;
    source = { kind, refs };
    openOptions = options;
    drafts.value = initialDrafts(created);
    shown.value = {};
    error.value = '';
    phase.value = 'review';
    return new Promise((resolve) => {
      settle = resolve;
    });
  }

  /**
   * Lot BQ : un geste dans le planning (glisser, clavier, confirmer une proposition). Le serveur dit si le plan
   * s'applique directement (`context.direct` : écritures internes au Cockpit, aucun rendez-vous fixé percuté) : pas
   * d'écran, la validation est le geste, tracée à l'audit, et « Annuler » reste offert pendant 5 secondes par le même
   * chemin serveur. Sinon, l'écran de validation s'ouvre.
   */
  async function runGesture(
    kind: PlanKind,
    refs: Record<string, unknown> = {},
    options: PlanOpenOptions = {},
  ): Promise<PlanRun | null> {
    if (!useOfflineStore().online) {
      toast.show(NET_ONLY_TOAST);
      return null;
    }
    let created: Plan;
    try {
      created = await api.createPlan({ kind, refs });
    } catch (err) {
      toast.show(errorMessage(err));
      return null;
    }
    options.onCreated?.(created);
    if (created.context.direct !== true) return present(created, kind, refs, options);
    const direct = initialDrafts(created);
    try {
      const run = await api.executePlan(created.id, {
        ...executeBody(created, direct),
        deferSeconds: PLAN_UNDO_SECONDS,
        gesture: true,
      });
      if (run.plan.status !== 'SCHEDULED') return run;
      return await new Promise<PlanRun | null>((resolve) => {
        void followDeferred(run, {
          drafts: direct,
          base: (r) => r.plan.title,
          resultsScreen: false,
          resolve,
          onRegistered: () => options.onScheduled?.(),
        });
      });
    } catch (err) {
      toast.show(errorMessage(err));
      return null;
    }
  }

  /**
   * Lot BQ : plan validé retrouvé après un rechargement de la page : « Annuler » revient avec le temps qu'il reste, et
   * le résultat se lit comme d'habitude.
   */
  function adopt(move: PendingMove, onDone?: (run: PlanRun | null) => void): void {
    if (scheduledPlans.value.some((s) => s.plan.id === move.plan.id)) return;
    const remaining = move.plan.scheduledFor ? Date.parse(move.plan.scheduledFor) - Date.now() : 0;
    const replay: StepDraft[] = move.plan.steps.map((step) => ({
      key: step.key,
      checked: move.checked.includes(step.key),
      edits: {},
    }));
    void followDeferred(
      {
        plan: move.plan,
        results: [],
        ...(move.plan.scheduledFor ? { scheduledFor: move.plan.scheduledFor } : {}),
        deferSeconds: Math.max(0, remaining) / 1000,
      },
      { drafts: replay, base: (r) => r.plan.title, resultsScreen: false, resolve: (run) => onDone?.(run) },
    );
  }

  async function reveal(run: PlanRun): Promise<void> {
    const reduced = reducedMotion();
    for (const result of run.results) {
      if (!reduced) await wait(STAGGER_MS);
      shown.value = { ...shown.value, [result.key]: result };
    }
  }

  /**
   * Lot BI : toast « <résumé> · Annuler » avec le compte à rebours, puis, à l'échéance, lecture du résultat.
   * Rend l'exécution réelle ; null si « Annuler » est arrivé à temps (rien n'est parti).
   */
  async function waitDeferred(scheduled: PlanRun, text: string): Promise<PlanRun | null> {
    const id = scheduled.plan.id;
    const ms = (scheduled.deferSeconds ?? PLAN_UNDO_SECONDS) * 1000;
    pending.value = [...pending.value, id];
    try {
      let clicked: () => void = () => undefined;
      const undo = new Promise<'undo'>((resolve) => {
        clicked = () => resolve('undo');
      });
      // Lot BQ : « Annuler » remet tout de suite la barre à sa place, sans attendre la réponse du serveur.
      let entry: ScheduledPlan | undefined;
      pushUndo({
        id,
        text,
        endsAt: Date.now() + ms,
        run: () => {
          entry = scheduledPlans.value.find((s) => s.plan.id === id);
          scheduledPlans.value = scheduledPlans.value.filter((s) => s.plan.id !== id);
          clicked();
        },
      });
      const outcome = await Promise.race([undo, wait(ms).then(() => 'due' as const)]);
      removeUndo(id);
      if (outcome === 'undo') {
        try {
          await api.undoPlan(id);
          if (undoStack.length === 0) toast.show(UNDONE_TOAST);
          return null;
        } catch (err) {
          // Trop tard : le plan part quand même, la barre garde donc sa nouvelle place.
          if (entry) scheduledPlans.value = [...scheduledPlans.value, entry];
          toast.show(
            err instanceof ApiError && err.status === 409
              ? TOO_LATE_TOAST
              : `Annulation impossible (${errorMessage(err)}) : l'action va partir.`,
            4000,
          );
        }
      }
      return await deferredResult(scheduled);
    } finally {
      pending.value = pending.value.filter((p) => p !== id);
    }
  }

  /** Lot BQ : le toast montre l'« Annuler » le plus récent, avec le temps qu'il lui reste. */
  function showTopUndo(): void {
    const top = undoStack.at(-1);
    if (!top) {
      if (undoTicket !== null) toast.hide(undoTicket);
      undoTicket = null;
      return;
    }
    const ms = Math.max(0, top.endsAt - Date.now());
    const others = undoStack.length - 1;
    const text = others > 0 ? `${top.text} (${others} autre${others > 1 ? 's' : ''} en attente)` : top.text;
    undoTicket = toast.show(text, ms + 300, null, {
      action: { label: 'Annuler', run: top.run },
      countdownMs: ms,
    });
  }

  function pushUndo(entry: UndoEntry): void {
    undoStack.push(entry);
    showTopUndo();
  }

  function removeUndo(id: string): void {
    const index = undoStack.findIndex((e) => e.id === id);
    if (index < 0) return;
    const wasTop = index === undoStack.length - 1;
    undoStack.splice(index, 1);
    if (wasTop || undoStack.length > 0) showTopUndo();
  }

  /** Lot BQ : abonnement à la fin d'un plan différé (exécuté ou annulé). */
  function onSettled(hook: (planId: string, plan: Plan) => Promise<void> | void): void {
    settledHooks.push(hook);
  }

  function dropScheduled(planId: string): void {
    scheduledPlans.value = scheduledPlans.value.filter((s) => s.plan.id !== planId);
  }

  async function notifySettled(planId: string, plan: Plan): Promise<void> {
    if (settledHooks.length === 0) {
      dropScheduled(planId);
      return;
    }
    for (const hook of settledHooks) await hook(planId, plan);
  }

  /** Lecture du plan après l'échéance, jusqu'à son résultat ; à défaut, le dernier état connu. */
  async function deferredResult(scheduled: PlanRun): Promise<PlanRun> {
    let last = scheduled;
    for (const ms of RESULT_POLL_MS) {
      await wait(ms);
      try {
        last = await api.getPlan(scheduled.plan.id);
        // Fini quand les conséquences (règles lancées après l'exécution) sont enregistrées.
        if (isFinishedStatus(last.plan.status) && last.consequences !== undefined) return last;
        if (last.plan.status === 'CANCELLED') return last;
      } catch {
        // Réseau ou serveur indisponible un instant : on réessaie.
      }
    }
    if (isFinishedStatus(last.plan.status)) return last;
    toast.show(
      "L'action est partie ; son résultat n'est pas encore connu : recharge l'écran dans un instant.",
      6000,
    );
    return last;
  }

  /**
   * Lot BI : fin d'un plan programmé (écran de validation, action directe, fenêtre mail). Comme après une
   * exécution : toast des conséquences avec « Voir », et pour un plan avec des conséquences, un lien rendu ou un
   * échec, l'écran de résultats rouvert (s'il est libre) ; la promesse de l'écran se résout à sa fermeture.
   */
  function finishDeferred(run: PlanRun | null, followUp: DeferredFollowUp, shownText: string): void {
    if (!run || !isFinishedStatus(run.plan.status)) {
      followUp.resolve(run && run.plan.status === 'CANCELLED' ? null : run);
      return;
    }
    const found = run.consequences ?? [];
    const text = runToastText(run, followUp.drafts, auth.me?.id ?? null, followUp.base?.(run));
    const needsScreen = run.plan.status !== 'DONE' || found.length > 0 || runHasResultLink(run);
    if (needsScreen && followUp.resultsScreen && phase.value === 'closed') {
      toast.show(text, found.length > 0 ? 5000 : 3200);
      plan.value = run.plan;
      drafts.value = followUp.drafts;
      shown.value = Object.fromEntries(run.results.map((r) => [r.key, r]));
      consequences.value = found;
      error.value = '';
      lastRun = run;
      settle = followUp.resolve;
      phase.value = 'results';
      return;
    }
    // Tout est fait comme annoncé : le toast du délai a déjà tout dit.
    if (text !== shownText) {
      const link = consequencesLink(run);
      toast.show(text, link ? 6000 : 3200, link);
    }
    followUp.resolve(run);
  }

  /** Lot BI : suite commune d'un plan programmé (appelée aussi par la fenêtre mail). */
  async function followDeferred(scheduled: PlanRun, followUp: DeferredFollowUp): Promise<void> {
    const predicted = predictedRun(scheduled, followUp.drafts);
    const text = runToastText(predicted, followUp.drafts, auth.me?.id ?? null, followUp.base?.(predicted));
    const ms = (scheduled.deferSeconds ?? PLAN_UNDO_SECONDS) * 1000;
    scheduledPlans.value = [
      ...scheduledPlans.value.filter((s) => s.plan.id !== scheduled.plan.id),
      {
        plan: scheduled.plan,
        checked: followUp.drafts.filter((d) => d.checked).map((d) => d.key),
        endsAt: Date.now() + ms,
      },
    ];
    followUp.onRegistered?.();
    const run = await waitDeferred(scheduled, text);
    // Lot BQ : la surcouche reste jusqu'à la relecture du planning (les vraies dates remplacent sans saut).
    await notifySettled(scheduled.plan.id, run?.plan ?? scheduled.plan);
    finishDeferred(run, followUp, text);
  }

  async function execute(): Promise<void> {
    const current = plan.value;
    if (!current || phase.value !== 'review') return;
    if (!drafts.value.some((d) => d.checked)) {
      toast.show('Rien de coché : rien ne sera fait.');
      return;
    }
    phase.value = 'executing';
    error.value = '';
    let run: PlanRun;
    try {
      run = await api.executePlan(current.id, {
        ...executeBody(current, drafts.value),
        deferSeconds: PLAN_UNDO_SECONDS,
      });
    } catch (err) {
      error.value = errorMessage(err);
      // Erreur de validation : le plan reste un brouillon qu'on peut corriger ; sinon il est perdu.
      phase.value = err instanceof ApiError && err.status === 400 ? 'review' : 'results';
      return;
    }
    if (run.plan.status === 'SCHEDULED') {
      // Lot BI : la fenêtre se ferme tout de suite ; rien ne part avant la fin du délai d'« Annuler ».
      const validated = drafts.value;
      const onScheduled = openOptions.onScheduled;
      const resolve = reset() ?? (() => undefined);
      await followDeferred(run, {
        drafts: validated,
        resultsScreen: true,
        resolve,
        onRegistered: () => onScheduled?.(),
      });
      return;
    }
    await reveal(run);
    consequences.value = run.consequences ?? [];
    phase.value = 'results';
    toast.show(
      runToastText(run, drafts.value, auth.me?.id ?? null),
      consequences.value.length > 0 ? 5000 : 3200,
    );
    // Tout est fait : on ferme comme la maquette ; sinon les résultats restent affichés. Lot T : avec des
    // conséquences, les résultats restent affichés pour les lire (« Fermer » ou un lien « Voir »). Lot AT : aussi
    // quand une étape rend un lien à ouvrir (« Ouvrir le devis dans Axonaut »).
    if (run.plan.status === 'DONE' && consequences.value.length === 0 && !runHasResultLink(run)) {
      await wait(reducedMotion() ? 50 : 450);
      if (plan.value?.id === current.id) close(run);
    } else {
      lastRun = run;
    }
  }

  /** « Annuler » (ou Échap) avant exécution : le plan est annulé côté serveur. */
  function cancel(): void {
    const current = plan.value;
    if (!current || phase.value === 'executing') return;
    if (phase.value === 'review') void api.cancelPlan(current.id).catch(() => undefined);
    close(phase.value === 'results' ? lastRun : null);
  }

  /**
   * Action directe (un seul geste = validation explicite) : création et exécution sans écran. Lot BI : « Annuler »
   * pendant 5 secondes aussi ; la promesse se résout avec l'exécution réelle, ou null si on a annulé.
   */
  async function runDirect(
    kind: PlanKind,
    refs: Record<string, unknown> = {},
    options: PlanOpenOptions = {},
  ): Promise<PlanRun | null> {
    if (!useOfflineStore().online) {
      toast.show(NET_ONLY_TOAST);
      return null;
    }
    try {
      const created = await api.createPlan({ kind, refs });
      const all = initialDrafts(created).map((d) => ({ ...d, checked: true }));
      const run = await api.executePlan(created.id, {
        ...executeBody(created, all),
        deferSeconds: PLAN_UNDO_SECONDS,
      });
      if (run.plan.status === 'SCHEDULED') {
        return await new Promise<PlanRun | null>((resolve) => {
          void followDeferred(run, {
            drafts: all,
            resultsScreen: false,
            resolve,
            onRegistered: () => options.onScheduled?.(),
          });
        });
      }
      // Lot T : pas d'écran de résultats ; le toast résume les conséquences, avec un lien vers « À faire ».
      const link = consequencesLink(run);
      toast.show(runToastText(run, all, auth.me?.id ?? null), link ? 6000 : 3200, link);
      return run;
    } catch (err) {
      toast.show(errorMessage(err));
      return null;
    }
  }

  /**
   * Reconstruit le plan affiché avec des références modifiées (lot K : date et heure du rendez-vous ; lot AL :
   * type de projet de « Préparer le projet »). L'ancien brouillon est annulé ; rien n'est exécuté.
   */
  async function rebuild(patch: Record<string, unknown>): Promise<void> {
    const current = plan.value;
    if (!current || !source || phase.value !== 'review' || rebuilding.value) return;
    rebuilding.value = true;
    try {
      const refs = { ...source.refs, ...patch };
      const created = await api.createPlan({ kind: source.kind, refs });
      void api.cancelPlan(current.id).catch(() => undefined);
      if (plan.value?.id !== current.id) return;
      source = { kind: source.kind, refs };
      plan.value = created;
      drafts.value = initialDrafts(created);
      error.value = '';
    } catch (err) {
      error.value = errorMessage(err);
    } finally {
      rebuilding.value = false;
    }
  }

  /**
   * Lot K (`context.slot`) : nouvelle date ou heure choisie dans l'écran de validation. Le plan est reconstruit
   * par le serveur avec `refs.date` / `refs.time` (le recalcul affiché dépend de la date).
   */
  function reschedule(date: string, time: string): Promise<void> {
    return rebuild({ date, time });
  }

  function toggle(key: string, checked: boolean): void {
    drafts.value = drafts.value.map((d) => (d.key === key ? { ...d, checked } : d));
  }
  function setChoice(key: string, choice: string): void {
    drafts.value = drafts.value.map((d) => (d.key === key ? { ...d, choice } : d));
  }
  function setEdit(key: string, field: string, value: string): void {
    drafts.value = drafts.value.map((d) =>
      d.key === key ? { ...d, edits: { ...d.edits, [field]: value } } : d,
    );
  }

  return {
    plan,
    drafts,
    phase,
    shown,
    consequences,
    error,
    pending,
    scheduledPlans,
    isOpen,
    rebuilding,
    openPlan,
    rebuild,
    reschedule,
    execute,
    cancel,
    runDirect,
    runGesture,
    adopt,
    pushUndo,
    removeUndo,
    onSettled,
    dropScheduled,
    followDeferred,
    toggle,
    setChoice,
    setEdit,
  };
});
